import axios from "axios";
import { CurrentUser } from "../helper/CurrentUser";
import { readCurrentUserId, withAgentUserId } from "./AgentRequestUser";

const BASE_URL = process.env.REACT_APP_CURRENT_MILAAPI;

class ApiClient {
  isAuthenticated = false;

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      headers: {
        "Content-Type": "application/json",
        "Referrer-Policy": "origin-when-cross-origin",
      },
    });

    this.client.interceptors.request?.use(
      (config) => {
        let userToken = CurrentUser.GetToken();

        if (userToken && userToken.access_token) {
          let at = `Bearer ${userToken.access_token}`;
          config.headers["Authorization"] = at;
        }

        if (userToken && userToken.refresh_token) {
          let rt = userToken.refresh_token;
          config.headers["Refresh-Token"] = rt;
        }

        // The agent gates on the server know the user only from the request (the session
        // carries the OU alone), so every agent route names the signed-in user — see
        // AgentRequestUser.js for why a single forgotten call site is a 404.
        return withAgentUserId(config, readCurrentUserId(CurrentUser));
      },
      (error) => Promise.reject(error)
    );

    this.client.response?.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        let userToken = CurrentUser.GetToken();
        if (
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;
          try {
            const refreshToken = userToken ? userToken.refresh_token : null;
            const response = await axios.post(
              `${BASE_URL}/MilaAI/RefreshToken`,
              {
                refreshToken,
              }
            );

            console.log(response.data.token);
            const { access_token, refresh_token } = response.data.token;

            if (userToken) {
              userToken.access_token = access_token;
              userToken.refresh_token = refresh_token;
            }

            CurrentUser.ReplaceToken(access_token, refresh_token);

            this.client.defaults.headers[
              "Authorization"
            ] = `Bearer ${access_token}`;
            originalRequest.headers["Authorization"] = `Bearer ${access_token}`;

            if (refresh_token) {
              this.client.defaults.headers["Refresh-Token"] = refresh_token;
              originalRequest.headers["Refresh-Token"] = refresh_token;
            }

            return this.client(originalRequest);
          } catch (err) {
            console.error("Token refresh failed:", err);
            localStorage.clear();
            window.location.href = "https://central.greenroad.com/";
            return Promise.reject(err);
          }
        }
        return Promise.reject(error);
      }
    );
    console.log(BASE_URL);
  }

  async login(username, password) {
    try {
      var login = { username: username, password: password };
      const response = await this.client.post("/MilaAI/Auth", login);
      if (response.data && response.data.token?.access_token) {
        this.isAuthenticated = true;
        localStorage.setItem("Token", JSON.stringify(response.data));
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Invalid credentials:", error);
      throw error;
    }
  }

  async createThreadId() {
    try {
      const response = await this.client.get("/MilaAI/CreateThreadId");
      if (response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Failed to create thread ID:", error);
      throw error;
    }
  }

  async autoSuggestions(question, userId, ouId, signal, limit = 6) {
    try {
      const response = await this.client.get(
        `/Suggestions/Retriever?q=${question}&userId=${userId}&ouId=${ouId}&limit=${limit}`,
        // axios will abort the request if the signal fires
        { signal }
      );
      if (response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      // axios throws CanceledError on abort
      if (error?.name === "CanceledError") return null;
      console.error("Failed to retrieve auto suggestions:", error);
      throw error;
    }
  }

  async isFileUploadActive() {
    try {
      const response = await this.client.get("/MilaAI/IsFileUploadActive");
      return response.data;
    } catch (error) {
      console.error("Failed to check file upload status:", error);
      throw error;
    }
  }

  async uploadFile(file, thread_id) {
    try {
      const formData = new FormData();
      formData.append('File', file);
      formData.append('Thread_Id', thread_id);

      let uploadUrl = "/MilaAI/UploadFileForThread";
      const response = await this.client.post(uploadUrl, formData, {
        headers: {
          'Accept': '*/*',
          'Content-Type': undefined,
        }
      });
      if (response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Upload failed with status:", error.status);
      throw error;
    }
  }

  async sendComment(comment) {
    try {
      const response = await this.client.post("/MilaAI/SendComment", comment);
      return response.data;
    } catch (error) {
      console.error("Failed to send comment:", error);
      throw error;
    }
  }

  async setMilaToken(milaToken) {
    try {
      const response = await this.client.post(
        "/MilaAI/SetMilaToken",
        milaToken
      );
      return response.data;
    } catch (error) {
      console.error("Failed to set mila token:", error);
      throw error;
    }
  }

  async saveHistoric(historic) {
    try {
      const response = await this.client.post(`/MilaAI/SaveHistoric`, historic);
      return response.data;
    } catch (error) {
      console.error("Failed to saveHistoric:", error);
      throw error;
    }
  }

  async setRecurrence(schedule) {
    try {
      let scheduleJson = JSON.stringify(schedule);
      scheduleJson = `"${scheduleJson}"`;
      //console.log(scheduleJson);
      const response = await this.client.post(
        `/MilaAI/SetRecurrence`,
        scheduleJson,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to setRecurrence:", error);
      throw error;
    }
  }

  async renameFavorite(favorite) {
    try {
      const response = await this.client.post(
        `/MilaAI/RenameFavorite`,
        favorite
      );
      return response.data;
    } catch (error) {
      console.error("Failed to renameFavorite:", error);
      throw error;
    }
  }

  async getSchedulableQuestions(questions) {
    try {
      const response = await this.client.post(
        `/MilaAI/GetSchedulableQuestions`, questions);
      return response.data;
    } catch (error) {
      console.error("Failed to getSchedulableQuestions:", error);
      throw error;
    }
  }

  async setFavorites(favorite) {
    try {
      const response = await this.client.post(
        `/MilaAI/MarkAsFavorite`,
        favorite
      );
      return response.data;
    } catch (error) {
      console.error("Failed to setFavorites:", error);
      throw error;
    }
  }

  async IsSessionValid() {
    try {
      const response = await this.client.get(`/MilaAI/IsSessionValid`);
      return response.data;
    } catch (error) {
      console.error("Failed to check connection readiness:", error);
      throw error;
    }
  }

  async checkFileUploadStatus(fileResponse) {
    try {
      const response = await this.client.get(
        `/MilaAI/CheckFileUploadStatus?background_job_id=${fileResponse?.background_job_id}&file_id=${fileResponse?.file_id}`
      );
      return response;
    } catch (error) {
      console.error("Failed to checkFileUploadStatus:", error);
      throw error;
    }
  }

  async deleteUploadedFile(file_id, id, scope) {
    try {
      const response = await this.client.get(
        `/MilaAI/DeleteUploadedFile?file_id=${file_id}&id=${id}&scope=${scope}`
      );
      return response;
    } catch (error) {
      console.error("Failed to deleteUploadedFile:", error);
      throw error;
    }
  }


  async removeRequestContext(requestId) {
    try {
      const response = await this.client.delete(
        `/MilaAI/RemoveRequestContext/${requestId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to start newDiscussion:", error);
      throw error;
    }
  }

  async deleteFavorite(id) {
    try {
      const response = await this.client.delete(`/MilaAI/DeleteFavorite/${id}`);
      return response.data;
    } catch (error) {
      console.error("Failed to deleteFavorite:", error);
      throw error;
    }
  }

  async getFavorites(userId) {
    try {
      const response = await this.client.get(`/MilaAI/GetFavorites/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to getFavorites:", error);
      throw error;
    }
  }

  // One chat answer as an .xlsx (base64): its text plus the structured tables its {{TABLE:n}}
  // placeholders point at. Empty when the answer has nothing to export.
  async exportResponseToExcel(text, tables, ouId, userId) {
    try {
      const response = await this.client.post(
        `/ResponseExport/Excel`,
        { response: text, tables, ouId, userId },
        // The server gives its agent a minute before exporting without it.
        { timeout: 120000 }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to exportResponseToExcel:", error);
      throw error;
    }
  }

  async scanNouns(sentence, userId, ouId, signal) {
    try {
      const response = await this.client.post(
        "/NounRecognition/Scan",
        { sentence, userId, ouId },
        { signal }
      );
      return response.data ?? null;
    } catch (error) {
      if (error?.name === "CanceledError") return null;
      console.error("Failed to scan nouns:", error);
      return null;
    }
  }

  async recordNounEvent(eventType, nounType, candidateText, confidenceScore, rootOU) {
    try {
      await this.client.post("/NounRecognition/Event", {
        eventType,
        nounType,
        candidateText,
        confidenceScore,
        rootOU,
      });
    } catch (error) {
      // Analytics events are best-effort — never throw
      console.error("Failed to record noun event:", error);
    }
  }

  async mentionSuggest(query, ouId, signal) {
    try {
      const response = await this.client.get("/NounRecognition/Mention", {
        params: { q: query, ouId },
        signal,
      });
      return response.data ?? [];
    } catch (error) {
      if (error?.name === "CanceledError") return [];
      console.error("Failed to fetch mention suggestions:", error);
      return [];
    }
  }

  async getRootOuId(ouId) {
    try {
      const response = await this.client.get(`/MilaAI/GetRootOuById/${ouId}`);
      return response.data;
    }
    catch (error) {
      console.error("Failed to getRootOuId:", error);
      throw error;
    }
  }

  async getMilaHistoric(userId) {
    try {
      const response = await this.client.get(
        `/MilaAI/GetMilaHistoric/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to getMilaHistoric:", error);
      throw error;
    }
  }

  async getMilaHistoricTableDatas(historicId) {
    try {
      const response = await this.client.get(
        `/MilaAI/GetMilaHistoricResources/${historicId}`
      );
      const data = response.data;

      //Extract the resource object containing tables.
      const tableEntries = [];

      data?.forEach(resource => {
        if (!resource.tableContent || !Array.isArray(resource.tableContent)) return;

        // Filter and add entries with type=table
        const tables = resource.tableContent.filter(entry => entry.type === 'table');
        tableEntries.push(...tables);
      });

      return tableEntries.length > 0 ? tableEntries : null;
    } catch (error) {
      console.error("Failed to GetMilaHistoricResources:", error);
      return null;
    }
  }

  async getTermsOfUse() {
    try {
      const response = await this.client.get(
        `/MilaAI/GetTermsOfUse`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to getTermsOfUse:", error);
      throw error;
    }
  }

  async getWaitingTexts() {
    let texts = [];
    texts.push("Thanks for waiting! I’m still working on the best answer for you. <br /><br />");
    texts.push("I’m on it! If this takes too long, feel free to try again in a moment. <br /><br />");
    return texts;
  }

  async getScheduledQuestions(userId) {
    try {
      const response = await this.client.get(
        `/MilaAI/GetScheduledQuestions/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to getScheduledQuestions:", error);
      throw error;
    }
  }

  // The page of a scheduled prompt's answers older than `beforeId` (the oldest one on screen),
  // as { responses, hasMore, oldestId }. The list opens the feed on its latest page and the
  // chat reads these as the reader scrolls up.
  async getScheduledResponses(scheduledQuestionId, beforeId, take = 26) {
    try {
      const response = await this.client.get(
        `/MilaAI/GetScheduledResponses/${scheduledQuestionId}`,
        { params: { beforeId, take } }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to getScheduledResponses:", error);
      throw error;
    }
  }

  async markScheduledQuestionAsRead(id) {
    try {
      const response = await this.client.get(
        `/MilaAI/MarkScheduledQuestionAsRead/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to markScheduledQuestionAsRead:", error);
      throw error;
    }
  }

  async deleteScheduledQuestion(id) {
    try {
      const response = await this.client.delete(
        `/MilaAI/DeleteScheduledQuestion/${id}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to deleteScheduledQuestion:", error);
      throw error;
    }
  }

  // Email delivery of a scheduled prompt: { sendToMe, ownerEmail, extraRecipients, configured }
  // ── Feature flags ───────────────────────────────────────────────────────
  // Flag key → on/off, as the back office set them. The app shows a feature only once this says
  // so; see FeatureFlagsContext for why a failure is `null` and not "everything off".
  /**
   * Which product features this deployment shows this user. The user id goes along because a
   * flag that is off may still be on for its test users; without one the answer is everyone's.
   */
  async getFeatureFlags(userId) {
    try {
      const response = await this.client.get(`/FeatureFlags`, {
        timeout: 8000,
        params: userId ? { userId } : undefined,
      });
      return response.data || {};
    } catch (error) {
      console.error("Failed to read the feature flags:", error);
      return null;
    }
  }

  async getScheduledEmailSettings(scheduledQuestionId) {
    try {
      const response = await this.client.get(`/ScheduledEmail/${scheduledQuestionId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to getScheduledEmailSettings:", error);
      throw error;
    }
  }

  // Replaces the whole settings object. A 400 carries the reason (a bad address, too many).
  async updateScheduledEmailSettings(scheduledQuestionId, settings) {
    try {
      const response = await this.client.put(`/ScheduledEmail/${scheduledQuestionId}`, settings);
      return response.data;
    } catch (error) {
      console.error("Failed to updateScheduledEmailSettings:", error);
      throw error;
    }
  }

  // With a userId the backend resolves in priority order: onboarding preferences →
  // the user's own most-asked recent questions → the back-office curated list.
  async getSuggestedQuestions(userId) {
    try {
      const url = userId
        ? `/MilaAI/GetSuggestedQuestions/${userId}`
        : `/MilaAI/GetSuggestedQuestions`;
      const response = await this.client.get(url);
      return response.data;
    } catch (error) {
      console.error("Failed to GetSuggestedQuestions:", error);
      throw error;
    }
  }

  // Feeds the back-office "Last access" column. Never throws: a lost stamp costs one row's
  // freshness, a thrown one would put an error in front of a user who did nothing wrong.
  async recordAccess(userId) {
    try {
      await this.client.post(`/MilaAI/RecordAccess/${userId}`);
    } catch (error) {
      console.error("Failed to record access:", error);
    }
  }

  async getTermsAcceptanceStatus(userId) {
    try {
      const response = await this.client.get(`/MilaAI/StatusTerms/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to get terms acceptance status:", error);
      throw error;
    }
  }

  async acceptTerms(userId) {
    try {
      const response = await this.client.post(`/MilaAI/AcceptTerms`, {
        accepted: true,
        userId: userId,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to accept terms:", error);
      throw error;
    }
  }

  // ── Onboarding (AskMila in-product onboarding) ──────────────────────────

  // `language` is the browser tag ("en-GB"): the backend deduces the `dialect` setting from it,
  // now that ai_context no longer carries response_language.
  async getOnboardingStatus(userId, ouId, language) {
    try {
      const response = await this.client.get(`/Onboarding/Status`, {
        params: { userId, ouId, language },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to get onboarding status:", error);
      return null;
    }
  }

  async startOnboarding(userId, ouId, language) {
    try {
      const response = await this.client.post(`/Onboarding/Start`, {
        userId,
        ouId,
        language,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to start onboarding:", error);
      throw error;
    }
  }

  async declineOnboarding(userId) {
    try {
      await this.client.post(`/Onboarding/Decline`, { userId });
      return true;
    } catch (error) {
      console.error("Failed to decline onboarding:", error);
      return false;
    }
  }

  async resumeOnboarding(userId) {
    try {
      const response = await this.client.post(`/Onboarding/Resume`, { userId });
      return response.data;
    } catch (error) {
      console.error("Failed to resume onboarding:", error);
      return null;
    }
  }

  async skipOnboarding(userId) {
    try {
      await this.client.post(`/Onboarding/Skip`, { userId });
      return true;
    } catch (error) {
      console.error("Failed to skip onboarding:", error);
      return false;
    }
  }

  // Timeout-bounded: this call fans out to the Mila AI settings API, and the settings modal shows
  // a spinner until it answers — it must fail fast rather than hang the popup.
  async getOnboardingProfile(userId, ouId, language) {
    try {
      const response = await this.client.get(`/Onboarding/Profile`, {
        params: { userId, ouId, language },
        timeout: 20000,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to get onboarding profile:", error);
      return null;
    }
  }

  async getOnboardingProfileOptions() {
    try {
      const response = await this.client.get(`/Onboarding/ProfileOptions`, {
        timeout: 20000,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to get onboarding profile options:", error);
      return null;
    }
  }

  async updateOnboardingProfile(userId, fields) {
    try {
      const response = await this.client.put(`/Onboarding/Profile`, {
        userId,
        fields,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to update onboarding profile:", error);
      throw error;
    }
  }

  // ── Agent platform (§15.8) ──────────────────────────────────────────────
  // The rail renders whatever this returns and has no hard-coded agent list, which is what
  // keeps shipping agent #2 to a registry row, a grant, and a module registered by key.
  async getMyAgents(ouId, userId) {
    try {
      // The axios instance has no global timeout, so without one here a backend that accepts
      // the connection and never answers leaves this promise pending for the life of the tab —
      // and the rail, which is the only navigation, never appears and never retries. Eight
      // seconds is generous: the call is a registry read plus a grant read.
      const response = await this.client.get(`/Agents/Mine`, {
        params: { ouId, userId },
        timeout: 8000,
      });
      return response.data || [];
    } catch (error) {
      // AskMila must keep working when the agent platform does not — but `null` rather than
      // `[]`, because "the call failed" and "you have no agents" produce very different UI and
      // the caller is the only one that can tell them apart. Returning `[]` here silently
      // removed the rail on any hiccup, with nothing to retry.
      console.error("Failed to resolve the agent rail:", error);
      return null;
    }
  }

  // ── Accident Prevention Agent (§12.2) ───────────────────────────────────
  // Every route here 404s rather than 403s without a grant, so a missing agent is
  // indistinguishable from one that does not exist.

  /**
   * `date` is left off when it is not given, and that is the point: which day is "today" belongs
   * to the OU's own time zone, and the browser knows only its own. The response says which day it
   * read, and the screen follows that rather than the clock on the desk.
   */
  async getAccidentPreventionPlan(ouId, userId, date) {
    const response = await this.client.get(`/AccidentPrevention/Plan`, {
      params: date ? { ouId, userId, date } : { ouId, userId },
      // 202 means "still preparing"; the caller polls rather than treating it as an error.
      validateStatus: (status) => status === 200 || status === 202,
    });
    return { status: response.status, data: response.data };
  }

  /**
   * Which lists this champion may open, and where the screen should open (PRD v6 req 59–61).
   * The server cuts the lists that exist to the champion's own OU and descendants; the client
   * renders what comes back and decides nothing itself.
   */
  async getAccidentPreventionLists(userId, date) {
    const response = await this.client.get(`/AccidentPrevention/Lists`, {
      params: date ? { userId, date } : { userId },
    });
    return response.data;
  }

  /** Which past days hold a list — the date navigator's calendar. A counting read. */
  async getAccidentPreventionPlanCalendar(ouId, userId, from, to) {
    const response = await this.client.get(`/AccidentPrevention/Plan/Calendar`, {
      params: { ouId, userId, from, to },
    });
    return response.data;
  }

  async reorderAccidentPreventionPlan(planId, ouId, userId, orderedItemIds) {
    const response = await this.client.post(`/AccidentPrevention/Plan/Reorder`, {
      planId,
      ouId,
      userId,
      orderedItemIds,
    });
    return response.data;
  }

  async removeAccidentPreventionItem(planItemId, ouId, userId, reason) {
    const response = await this.client.post(`/AccidentPrevention/Plan/Remove`, {
      planItemId,
      ouId,
      userId,
      reason,
    });
    return response.data;
  }

  // The reason is required (req 10): it becomes the driver's reason line on the list.
  async addAccidentPreventionDriver(planId, ouId, userId, driverId, reason) {
    const response = await this.client.post(`/AccidentPrevention/Plan/AddDriver`, {
      planId,
      ouId,
      userId,
      driverId,
      reason,
    });
    return response.data;
  }

  // The first-run walkthrough has run for this champion and must not run again (req 57).
  async markAccidentPreventionWalkthroughSeen(ouId, userId) {
    const response = await this.client.post(`/AccidentPrevention/Walkthrough/Seen`, {
      ouId,
      userId,
    });
    return response.data;
  }

  async setAccidentPreventionInterventionType(planItemId, ouId, userId, type) {
    const response = await this.client.put(
      `/AccidentPrevention/Plan/Item/${planItemId}/InterventionType`,
      { ouId, userId, type }
    );
    return response.data;
  }

  async getAccidentPreventionDriverCard(driverId, ouId, userId, date) {
    const response = await this.client.get(
      `/AccidentPrevention/Driver/${driverId}/Card`,
      { params: { ouId, userId, date } }
    );
    return response.data;
  }

  async searchAccidentPreventionDrivers(ouId, term, take = 20) {
    try {
      const response = await this.client.get(`/AccidentPrevention/Driver/Search`, {
        params: { ouId, term, take },
      });
      return response.data || [];
    } catch (error) {
      console.error("Driver search failed:", error);
      return [];
    }
  }

  // The mark alone is a valid record; the note is optional and has no length limit (req 14).
  async recordAccidentPreventionIntervention(payload) {
    const response = await this.client.post(`/AccidentPrevention/Intervention`, payload);
    return response.data;
  }

  // Takes the mark back off. Idempotent, so a double tap of the tick is not an error.
  async undoAccidentPreventionIntervention(payload) {
    const response = await this.client.post(`/AccidentPrevention/Intervention/Undo`, payload);
    return response.data;
  }

  async getAccidentPreventionPolicy(ouId) {
    const response = await this.client.get(`/AccidentPrevention/Policy`, {
      params: { ouId },
    });
    return response.data;
  }

  async upsertAccidentPreventionPolicyRule(payload) {
    const response = await this.client.put(`/AccidentPrevention/Policy/Rule`, payload);
    return response.data;
  }

  // There is no activate and no deactivate (req 17): a typed rule is in force when saved, a
  // document's rules are confirmed in the review pop-up, and delete is the way out.
  async deleteAccidentPreventionPolicyRule(ruleId, ouId, userId) {
    const response = await this.client.delete(
      `/AccidentPrevention/Policy/Rule/${ruleId}`,
      { params: { ouId, userId } }
    );
    return response.data;
  }

  // Where this policy could go, and what each of those depots holds now — a copy replaces, so
  // the dialog says what it is replacing before it is asked to.
  async getAccidentPreventionPolicyCopyTargets(ouId, userId) {
    const response = await this.client.get(`/AccidentPrevention/Policy/CopyTargets`, {
      params: { ouId, userId },
    });
    return response.data;
  }

  async copyAccidentPreventionPolicy(payload) {
    const response = await this.client.post(`/AccidentPrevention/Policy/Copy`, payload);
    return response.data;
  }

  async uploadAccidentPreventionPolicyDocument(file, ouId, userId) {
    const form = new FormData();
    form.append("file", file);
    form.append("ouId", ouId);
    form.append("userId", userId);

    const response = await this.client.post(`/AccidentPrevention/Policy/Document`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    });
    return response.data;
  }

  // The one confirm that turns a document's reviewed rules into rules (req 17). `rules` is the
  // proposals as the user left them in the pop-up; a proposal missing from it was deleted.
  async confirmAccidentPreventionPolicyDocument(documentId, payload) {
    const response = await this.client.post(
      `/AccidentPrevention/Policy/Document/${documentId}/Confirm`,
      payload,
      // Reworded proposals go back through the compiler, one call each.
      { timeout: 180000 }
    );
    return response.data;
  }

  // One file: the duty roster, which carries its own driver allocation.
  async uploadAccidentPreventionRoster(file, ouId, userId) {
    const form = new FormData();
    form.append("file", file);
    form.append("ouId", ouId);
    form.append("userId", userId);

    const response = await this.client.post(`/AccidentPrevention/Roster/Upload`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000,
    });
    return response.data;
  }

  // Nothing is normalized until this call: a file the agent misreads produces a stated
  // reading the user corrects, never a silently wrong day (req 30).
  async confirmAccidentPreventionRosterMapping(uploadId, payload) {
    const response = await this.client.post(
      `/AccidentPrevention/Roster/${uploadId}/ConfirmMapping`,
      payload,
      { timeout: 300000 }
    );
    return response.data;
  }

  async getAccidentPreventionRosterStatus(ouId, date) {
    const response = await this.client.get(`/AccidentPrevention/Roster/Status`, {
      params: { ouId, date },
    });
    return response.data;
  }

  // The duty store as the parser left it: every upload still shaping the day views, newest
  // computation first, each with the dates it still covers. What the Duty Manager lists.
  async getAccidentPreventionDutyCatalog(ouId) {
    const response = await this.client.get(`/AccidentPrevention/Roster/Duties`, {
      params: { ouId },
    });
    return response.data;
  }

  // One upload's standing duties, fetched when its catalog row is expanded.
  async getAccidentPreventionDutyCatalogDuties(uploadId, ouId) {
    const response = await this.client.get(`/AccidentPrevention/Roster/Duties/${uploadId}`, {
      params: { ouId },
    });
    return response.data;
  }

  // One duty's parts — legs, breaks, layovers — in the order they are worked, fetched when a
  // duty row is opened in the Duty Manager.
  async getAccidentPreventionDutySegments(dutyId, ouId) {
    const response = await this.client.get(`/AccidentPrevention/Roster/Duty/${dutyId}/Segments`, {
      params: { ouId },
    });
    return response.data;
  }

  // Takes one upload's duties and driver-days back out of the store. Answers with the catalog
  // as it stands afterwards, so the manager reconciles from one response.
  async removeAccidentPreventionDutyUpload(uploadId, ouId, userId) {
    const response = await this.client.delete(`/AccidentPrevention/Roster/Duties/${uploadId}`, {
      params: { ouId, userId },
    });
    return response.data;
  }

  /**
   * The OU's settings with the champion's own daily number and window folded in (PRD v6 req 62):
   * one form, two owners, which the server splits on save.
   */
  async getAccidentPreventionSettings(ouId, userId) {
    const response = await this.client.get(`/AccidentPrevention/Settings`, {
      params: userId ? { ouId, userId } : { ouId },
    });
    return response.data;
  }

  async updateAccidentPreventionSettings(payload) {
    const response = await this.client.put(`/AccidentPrevention/Settings`, payload);
    return response.data;
  }

  async cancelTerms(userId) {
    try {
      const response = await this.client.post(`/MilaAI/CancelTerms`, {
        accepted: false,
        userId: userId,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to cancel terms:", error);
      throw error;
    }
  }
}

const apiClient = new ApiClient();
export default apiClient;
